import { supabase } from '@shared/lib/supabase/client'
import type { Database } from '@shared/types/database'

type Wetsartikel = Database['public']['Tables']['wetsartikelen']['Row']

export interface ParsedArtikel {
  artikel_nummer: string
  lid?: string
  sub_onderdeel?: string
  wetboek: string
  wetboek_afkorting: string
  volledige_referentie: string
}

class WetsartikelenService {
  /**
   * Get all wetsartikelen without counts
   */
  async getAll(): Promise<Wetsartikel[]> {
    const { data, error } = await supabase
      .from('wetsartikelen')
      .select('*')
      .order('artikel_nummer')
      .order('lid')
      .order('sub_onderdeel')

    if (error) {
      console.error('Error fetching wetsartikelen:', error)
      return []
    }

    return data || []
  }

  /**
   * Get wetsartikelen by wetboek afkorting (e.g., "BW", "Sv")
   */
  async getByWetboek(wetboekAfkorting: string): Promise<Wetsartikel[]> {
    const { data, error } = await supabase
      .from('wetsartikelen')
      .select('*')
      .eq('wetboek_afkorting', wetboekAfkorting)
      .order('artikel_nummer')
      .order('lid')
      .order('sub_onderdeel')

    if (error) {
      console.error('Error fetching wetsartikelen by wetboek:', error)
      return []
    }

    return data || []
  }

  /**
   * Get wetsartikel by ID
   */
  async getById(id: string): Promise<Wetsartikel | null> {
    const { data, error } = await supabase
      .from('wetsartikelen')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching wetsartikel:', error)
      return null
    }

    return data
  }

  /**
   * Get wetsartikelen with case counts, optionally filtered by other criteria
   * Follows the same pattern as rechtsgebieden.service.ts
   */
  async getWithCaseCount(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    instantieIds?: string[]
    zaakTypeIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<(Wetsartikel & { case_count: number })[]> {
    // Use provided portal IDs or empty array (database will use default)
    const portalIds = filters?.portalIds || [];

    // Try search-enabled RPC function first if search query is provided
    if (filters?.searchQuery) {
      const { data, error } = await (supabase
        .rpc as any)('get_wetsartikelen_with_search_counts', {
          search_query: filters.searchQuery,
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_instantie_ids: filters.instantieIds || null,
          filter_zaak_type_ids: filters.zaakTypeIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching search-filtered wetsartikelen counts:', error)
    }

    // Try filtered RPC function without search
    if (filters && (filters.rechtsgebiedIds?.length || filters.instantieIds?.length || filters.zaakTypeIds?.length || filters.dateFrom || filters.dateTo)) {
      const { data, error } = await (supabase
        .rpc as any)('get_wetsartikelen_with_filtered_counts', {
          filter_rechtsgebied_ids: filters.rechtsgebiedIds || null,
          filter_instantie_ids: filters.instantieIds || null,
          filter_zaak_type_ids: filters.zaakTypeIds || null,
          filter_date_from: filters.dateFrom || null,
          filter_date_to: filters.dateTo || null,
          p_portals: portalIds
        })

      if (!error) {
        return (data || []).map((item: any) => ({
          ...item,
          case_count: Number(item.case_count) || 0
        }))
      }
      console.error('Error fetching filtered wetsartikelen counts:', error)
    }

    // Fallback to unfiltered RPC function
    const { data, error } = await (supabase
      .rpc as any)('get_wetsartikelen_with_counts', {
        p_portals: portalIds
      })

    if (error) {
      console.error('Error fetching wetsartikelen with counts:', error)
      // Fallback to regular fetch without counts
      const { data: fallbackData } = await supabase
        .from('wetsartikelen')
        .select('*')
        .order('artikel_nummer')
        .order('lid')
        .order('sub_onderdeel')

      return (fallbackData || []).map(item => ({
        ...item,
        case_count: 0
      }))
    }

    return (data || []).map((item: any) => ({
      ...item,
      case_count: Number(item.case_count) || 0
    }))
  }

  /**
   * Get wetsartikelen hierarchy with aggregated counts at wetboek and artikel level
   * This prevents double counting when a jurisprudentie references multiple sub-artikelen
   */
  async getHierarchyWithCounts(filters?: {
    searchQuery?: string
    rechtsgebiedIds?: string[]
    instantieIds?: string[]
    zaakTypeIds?: string[]
    dateFrom?: string
    dateTo?: string
    portalIds?: string[]
  }): Promise<{
    wetboek: string
    wetboek_afkorting: string
    artikel_nummer: string
    wetsartikel_id: string
    volledige_referentie: string
    lid: string | null
    sub_onderdeel: string | null
    item_case_count: number
    artikel_case_count: number
    wetboek_case_count: number
  }[]> {
    // Use provided portal IDs or empty array (database will use default)
    const portalIds = filters?.portalIds || [];

    // RPC returns JSONB (scalar) to bypass PostgREST max_rows=1000 truncation.
    // .single() tells Supabase JS to expect a single scalar value instead of a result set.
    const { data, error } = await (supabase.rpc as any)('get_wetsartikelen_hierarchy_with_counts', {
      search_query: filters?.searchQuery || null,
      filter_rechtsgebied_ids: filters?.rechtsgebiedIds || null,
      filter_instantie_ids: filters?.instantieIds || null,
      filter_zaak_type_ids: filters?.zaakTypeIds || null,
      filter_date_from: filters?.dateFrom || null,
      filter_date_to: filters?.dateTo || null,
      p_portals: portalIds
    }).single()

    if (error) {
      console.error('Error fetching wetsartikelen hierarchy with counts:', error)
      return []
    }

    return (data || []).map((item: any) => ({
      wetboek: item.wetboek,
      wetboek_afkorting: item.wetboek_afkorting,
      artikel_nummer: item.artikel_nummer,
      wetsartikel_id: item.wetsartikel_id,
      volledige_referentie: item.volledige_referentie,
      lid: item.lid,
      sub_onderdeel: item.sub_onderdeel,
      item_case_count: Number(item.item_case_count) || 0,
      artikel_case_count: Number(item.artikel_case_count) || 0,
      wetboek_case_count: Number(item.wetboek_case_count) || 0
    }))
  }

  /**
   * Get or create a wetsartikel
   * Used by the Azure OpenAI function when extracting wetsartikelen from case text
   */
  async getOrCreateWetsartikel(artikel: ParsedArtikel): Promise<string | null> {
    try {
      // Check if wetsartikel already exists
      let query = supabase
        .from('wetsartikelen')
        .select('id')
        .eq('artikel_nummer', artikel.artikel_nummer)
        .eq('wetboek_afkorting', artikel.wetboek_afkorting)

      if (artikel.lid) {
        query = query.eq('lid', artikel.lid)
      } else {
        query = query.is('lid', null)
      }

      if (artikel.sub_onderdeel) {
        query = query.eq('sub_onderdeel', artikel.sub_onderdeel)
      } else {
        query = query.is('sub_onderdeel', null)
      }

      const { data: existing } = await query.single()

      if (existing) {
        return existing.id
      }

      // Create new wetsartikel
      const { data: newArtikel, error } = await supabase
        .from('wetsartikelen')
        .insert({
          artikel_nummer: artikel.artikel_nummer,
          lid: artikel.lid || null,
          sub_onderdeel: artikel.sub_onderdeel || null,
          wetboek: artikel.wetboek,
          wetboek_afkorting: artikel.wetboek_afkorting,
          volledige_referentie: artikel.volledige_referentie
        })
        .select('id')
        .single()

      if (error) {
        console.error('Error creating wetsartikel:', error)
        return null
      }

      console.log(`✅ Wetsartikel aangemaakt: ${artikel.volledige_referentie}`)
      return newArtikel?.id || null
    } catch (error) {
      console.error('Error in getOrCreateWetsartikel:', error)
      return null
    }
  }

  /**
   * Link a wetsartikel to a jurisprudentie case
   * Used by the Azure OpenAI function
   */
  async linkToJurisprudentie(jurisprudentieId: string, wetsartikelId: string, context?: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('jurisprudentie_wetsartikelen')
        .insert({
          jurisprudentie_id: jurisprudentieId,
          wetsartikel_id: wetsartikelId,
          context: context || null
        })

      if (error) {
        // Ignore duplicate key violations (already linked)
        if (error.code === '23505') {
          return true
        }
        console.error('Error linking wetsartikel to jurisprudentie:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error in linkToJurisprudentie:', error)
      return false
    }
  }

  /**
   * Get wetsartikelen for a specific jurisprudentie case
   */
  async getByJurisprudentieId(jurisprudentieId: string): Promise<Wetsartikel[]> {
    const { data, error } = await supabase
      .from('jurisprudentie_wetsartikelen')
      .select('wetsartikelen (*)')
      .eq('jurisprudentie_id', jurisprudentieId)

    if (error) {
      console.error('Error fetching wetsartikelen for jurisprudentie:', error)
      return []
    }

    return (data || []).map((item: any) => item.wetsartikelen).filter(Boolean)
  }
}

export const wetsartikelenService = new WetsartikelenService()